import { useTranslation } from "react-i18next";
import Loader from "../../../../components/Loader";
import { Switch } from "../../../../components/ui/Switch";
import { openDesktopSettings } from "../../../../domains/app/stores/desktopSettingsStore";
import { PartnersList } from "../../../../components/partners/PartnersList";
import { usePartnerSharing } from "../../../../domains/partners/usePartnerSharing";
import { cn } from "../../../../lib/utils";
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
} from "../components/SettingsPanel";

/**
 * "Partner sharing" (Figma D3.settings.partner-sharing, 5154:92381; O4 and
 * docs/partner-objection.md REQ-09): one switch for all partners, present and
 * future, then one switch for each partner of the rooms of the user. A switch
 * shows the state: on, the partner receives the email address; off, an
 * objection applies. A partner that owns two rooms has one switch (AC-09).
 */
export function PartnerSharingSection(): React.JSX.Element {
  const { t } = useTranslation();
  const { sharingQuery, partnersQuery, isSaving, changeSharing } =
    usePartnerSharing();

  return (
    <SettingsPage back={null}>
      <section className="flex flex-col gap-2.5">
        <h2 className="text-heading-xs font-semibold text-ds-slate-950">
          {t("Partner sharing")}
        </h2>
        <p className="m-0 text-sm text-ds-slate-700">
          {t(
            "When you join a room a partner supports, the partner receives your email address and may contact you for professional communications. Turning sharing off doesn't change your access to content."
          )}
        </p>
      </section>

      {sharingQuery.state === "pending" ? (
        <Loader expanded />
      ) : sharingQuery.state === "error" ? (
        <p className="text-sm text-ds-slate-700">
          {t("Something went wrong. Please try again.")}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-4 rounded-xl bg-ds-slate-50 px-4 py-3.5">
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-ui-body font-medium text-ds-slate-950">
                {t("Share my email with partners")}
              </span>
              <span className="text-desktop-meta text-ds-slate-700">
                {t("All partners, including future ones. Turn off to object.")}
              </span>
            </span>
            <Switch
              checked={sharingQuery.body.sharing}
              label={t("Share my email with partners")}
              disabled={isSaving}
              onCheckedChange={(sharing) => {
                changeSharing(null, sharing);
              }}
            />
          </div>

          <div className="flex items-center gap-2 text-sm font-medium">
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent p-0 text-ds-mint-700 hover:underline"
              onClick={() => {
                openDesktopSettings({ page: "partners" });
              }}
            >
              {partnersQuery.state === "success"
                ? t("See our {{n}} partners", {
                    n: partnersQuery.body.length,
                  })
                : t("See our partners")}
            </button>
            <span className="text-ds-slate-700">·</span>
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent p-0 text-ds-mint-700 hover:underline"
              onClick={() => {
                // The privacy policy page of the settings (Figma D3,
                // 5580:171368): never a dialog over the settings modal.
                openDesktopSettings({ page: "privacy", tab: "juisci" });
              }}
            >
              {t("Privacy policy")}
            </button>
          </div>

          {sharingQuery.body.partners.length > 0 && (
            <SettingsSection
              title={t("Partner by partner")}
              description={t(
                "Turn a partner off to stop sharing your email address with it. You stay a member of its rooms."
              )}
            >
              {sharingQuery.body.partners.map((partner) => (
                <div
                  key={partner.organisationId}
                  // The global switch off objects for every partner: the
                  // partner rows wait for it (Figma O4, off for all).
                  className={cn(!sharingQuery.body.sharing && "opacity-50")}
                >
                  <SettingsRow
                    title={partner.name}
                    subtitle={t("Via {{rooms}}", {
                      rooms: partner.roomNames.join(", "),
                    })}
                    leading={
                      <img
                        src={partner.logoUrl}
                        alt=""
                        className="size-8 shrink-0 object-contain"
                      />
                    }
                    right={
                      <Switch
                        checked={partner.sharing}
                        label={partner.name}
                        disabled={!sharingQuery.body.sharing || isSaving}
                        onCheckedChange={(sharing) => {
                          changeSharing(partner.organisationId, sharing);
                        }}
                      />
                    }
                  />
                </div>
              ))}
            </SettingsSection>
          )}

          <p className="m-0 text-desktop-meta text-ds-slate-700">
            {t(
              "You stay a member of your rooms and keep access to their content."
            )}
          </p>
        </>
      )}
    </SettingsPage>
  );
}

/**
 * The partners of Juisci, opened from "See our partners" of "Partner sharing"
 * (Figma D3, 5580:171302): a page of the settings modal with its way back,
 * never a dialog over it.
 */
export function PartnersSection(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <SettingsPage
      back={{
        label: t("Partner sharing"),
        onBack: () => {
          openDesktopSettings({ page: "partnerSharing" });
        },
      }}
    >
      <section className="flex flex-col gap-2.5">
        <h2 className="text-heading-xs font-semibold text-ds-slate-950">
          {t("Our partners")}
        </h2>
        <PartnersList layout="desktop" policies={null} />
      </section>
    </SettingsPage>
  );
}
